import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from '@mui/material'
import {
  CreditCard as CreditCardIcon,
  ArrowBack as ArrowBackIcon,
  Payment as PaymentIcon,
  CurrencyRupee as CashIcon,
  QrCode as QrCodeIcon,
} from '@mui/icons-material'
import { useCurrency } from '../context/CurrencyContext'
import { createBooking } from '../services/bookingService'
import type { Room } from '../services/roomService'

interface LocationState {
  room: Room
  bookingDetails: {
    name: string
    email: string
    phone: string
    checkIn: string
    checkOut: string
    adults: number
    children: number
    specialRequests: string
  }
}

const PaymentPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { convertPrice } = useCurrency()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paymentMethod, setPaymentMethod] = useState('card')
  const [upiId, setUpiId] = useState('')
  
  const { room, bookingDetails } = location.state as LocationState

  // Calculate number of nights
  const calculateNights = () => {
    if (!bookingDetails.checkIn || !bookingDetails.checkOut) return 0
    const checkIn = new Date(bookingDetails.checkIn)
    const checkOut = new Date(bookingDetails.checkOut)
    return Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24))
  }

  // Calculate total price
  const calculateTotalPrice = () => {
    if (!room) return 0
    const nights = calculateNights()
    const pricePerNight = room.discountedPrice || room.basePrice
    return pricePerNight * nights
  }

  const handlePaymentMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentMethod(event.target.value)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!room) return

    // Validate UPI ID if UPI payment method is selected
    if (paymentMethod === 'upi' && !upiId.includes('@')) {
      setError('Please enter a valid UPI ID')
      return
    }

    setLoading(true)
    setError(null)
    
    try {
      const bookingData = {
        ...bookingDetails,
        roomId: room.id,
        roomType: room.type,
        totalAmount: calculateTotalPrice(),
        status: paymentMethod === 'cash' ? 'pending' : 'confirmed',
        paymentMethod,
        paymentStatus: paymentMethod === 'cash' ? 'pending' : 'paid'
      }
      
      await createBooking(bookingData)
      
      // Redirect to success page with payment info
      navigate('/booking-success', { 
        state: { 
          paymentMethod,
          totalAmount: calculateTotalPrice(),
          bookingDetails
        }
      })
    } catch (error) {
      setError('Payment failed. Please try again.')
      console.error('Payment error:', error)
    } finally {
      setLoading(false)
    }
  }

  const nights = calculateNights()
  const totalPrice = calculateTotalPrice()

  if (!room || !bookingDetails) {
    navigate('/rooms')
    return null
  }

  return (
    <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.default' }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Complete Payment
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Choose your payment method and complete the booking
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Payment Form */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <form onSubmit={handleSubmit}>
                <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentIcon color="primary" />
                  Payment Method
                </Typography>

                <FormControl component="fieldset">
                  <RadioGroup
                    value={paymentMethod}
                    onChange={handlePaymentMethodChange}
                  >
                    {/* Credit/Debit Card Option */}
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        mb: 2, 
                        p: 2,
                        border: paymentMethod === 'card' ? 2 : 1,
                        borderColor: paymentMethod === 'card' ? 'primary.main' : 'divider'
                      }}
                    >
                      <FormControlLabel
                        value="card"
                        control={<Radio />}
                        label={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CreditCardIcon color={paymentMethod === 'card' ? 'primary' : 'action'} />
                            <Typography>Credit/Debit Card</Typography>
                          </Stack>
                        }
                      />
                      {paymentMethod === 'card' && (
                        <Box sx={{ mt: 2, pl: 4 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                label="Card Number"
                                fullWidth
                                required
                                size="small"
                                inputProps={{ maxLength: 16 }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Expiry Date"
                                fullWidth
                                required
                                size="small"
                                placeholder="MM/YY"
                                inputProps={{ maxLength: 5 }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="CVV"
                                fullWidth
                                required
                                size="small"
                                type="password"
                                inputProps={{ maxLength: 3 }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                label="Name on Card"
                                fullWidth
                                required
                                size="small"
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Paper>

                    {/* UPI Option */}
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        mb: 2, 
                        p: 2,
                        border: paymentMethod === 'upi' ? 2 : 1,
                        borderColor: paymentMethod === 'upi' ? 'primary.main' : 'divider'
                      }}
                    >
                      <FormControlLabel
                        value="upi"
                        control={<Radio />}
                        label={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <QrCodeIcon color={paymentMethod === 'upi' ? 'primary' : 'action'} />
                            <Typography>UPI Payment</Typography>
                          </Stack>
                        }
                      />
                      {paymentMethod === 'upi' && (
                        <Box sx={{ mt: 2, pl: 4 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                label="UPI ID"
                                fullWidth
                                required
                                size="small"
                                placeholder="example@upi"
                                value={upiId}
                                onChange={(e) => setUpiId(e.target.value)}
                                helperText="Enter your UPI ID (e.g., name@bank)"
                              />
                            </Grid>
                          </Grid>
                          <Alert severity="info" sx={{ mt: 2 }}>
                            You will receive a payment request on your UPI app
                          </Alert>
                        </Box>
                      )}
                    </Paper>

                    {/* Cash on Delivery (Pay at Hotel) Option */}
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2,
                        border: paymentMethod === 'cash' ? 2 : 1,
                        borderColor: paymentMethod === 'cash' ? 'primary.main' : 'divider'
                      }}
                    >
                      <FormControlLabel
                        value="cash"
                        control={<Radio />}
                        label={
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <CashIcon color={paymentMethod === 'cash' ? 'primary' : 'action'} />
                            <Typography>Pay at Hotel</Typography>
                          </Stack>
                        }
                      />
                      {paymentMethod === 'cash' && (
                        <Box sx={{ mt: 2, pl: 4 }}>
                          <Alert severity="info">
                            Pay the full amount at the hotel during check-in. Please note that your booking will be confirmed but payment will be pending.
                          </Alert>
                        </Box>
                      )}
                    </Paper>
                  </RadioGroup>
                </FormControl>

                {error && (
                  <Alert severity="error" sx={{ mt: 3 }}>
                    {error}
                  </Alert>
                )}
              </form>
            </Paper>
          </Grid>

          {/* Payment Summary */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Payment Summary
              </Typography>

              <Box sx={{ mb: 3 }}>
                <img 
                  src={room.images[0]} 
                  alt={room.name}
                  style={{
                    width: '100%',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    marginBottom: '16px',
                  }}
                />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {room.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {room.type}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Rate per night
                  </Typography>
                  <Typography variant="body1">
                    {convertPrice(room.discountedPrice || room.basePrice)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Number of nights
                  </Typography>
                  <Typography variant="body1">
                    {nights}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Taxes & Fees (18% GST)
                  </Typography>
                  <Typography variant="body1">
                    {convertPrice(totalPrice * 0.18)}
                  </Typography>
                </Box>

                <Divider />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Total Amount
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {convertPrice(totalPrice * 1.18)}
                  </Typography>
                </Box>
              </Stack>

              <Button
                onClick={handleSubmit}
                disabled={loading}
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  mt: 3,
                  py: 1.5,
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : paymentMethod === 'cash' ? (
                  'Confirm Booking'
                ) : (
                  'Pay Now'
                )}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}

export default PaymentPage
